import React, { useEffect, useState } from "react";
import { Trophy, ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useScoresStore } from "../../store/scoresStore";
import useAuthStore from "../../store/authStore";
import { PlayerRecord } from "../../components/PlayerRecord";
import { useConfirmationDialog } from "../../components/ConfirmDialog";
import { v4 as uuid } from "uuid";
import { formatToTimeStamp, showDateTimeShort } from "../../utils/dateUtils";
import {
  FlightDetailsMatchPlay,
  ScoreMatchPlay,
} from "../../models/scoreModel";
import { MatchPlayScoreList } from "./MatchPlayScoreList";
import { getScoresBetweenPlayers, getWLDSingleHoleScore, PlayerScore } from "./MatchPlayCalculations";

export const MatchPlayScoreEntry: React.FC<any> = () => {
  const [currentHole, setCurrentHole] = useState(1);

  const { currentUser } = useAuthStore();
  const {
    allDetailsForScoreEntry,
    addMatchPlayScore,
    updateMatchPlayScore,
    uploadMatchPlayScoresByPlayer,
    getScoresToStartEntry,
  } = useScoresStore();
  const { openDialog } = useConfirmationDialog();

  const [playerScores, setPlayerScores] = useState<PlayerScore[]>(() => {
    const flightDetails =
      allDetailsForScoreEntry?.flightDetails as FlightDetailsMatchPlay;
    return (
      flightDetails?.players?.map((p) => ({
        playerId: p.playerId,
        score: 0,
        isConceded: false,
        playerHandicap: Number(p.player.playerHandicap),
      })) || []
    );
  });

  // Yardage data for each hole
  const getCurrentHoleData = (holeNumber: number) => {
    if (!allDetailsForScoreEntry) return null;
    const hole = allDetailsForScoreEntry.holes.find(
      (h: any) => h.holeNumber === holeNumber
    );
    return {
      holeNumber: hole?.holeNumber,
      par: hole?.par || 0,
      men: hole?.yardages.men.yardage || 0,
      senior: hole?.yardages.seniors.yardage || 0,
      ladies: hole?.yardages.ladies.yardage || 0,
      strokeIndex: hole?.strokeIndex || 0,
    };
  };

  const handlePrevHole = () => {
    handleSaveScore(false);

    if (currentHole > 1) {
      setCurrentHole((prev) => prev - 1);
    } else if (currentHole === 1) {
      setCurrentHole(18);
    }
  };

  const handleNextHole = (shouldSave = false) => {
    if (shouldSave) {
      handleSaveScore(false);
    }

    if (currentHole < 18) {
      setCurrentHole((prev) => prev + 1);
    } else if (currentHole === 18) {
      setCurrentHole(1);
    }
  };

  useEffect(() => {
    if (currentUser) {
      getScoresToStartEntry(currentUser.playerId ?? "");
    }
  }, [currentUser, getScoresToStartEntry]);

  useEffect(() => {
    if (allDetailsForScoreEntry && allDetailsForScoreEntry.scores.length > 0) {
      const flightDetails =
        allDetailsForScoreEntry.flightDetails as FlightDetailsMatchPlay;
      const scores = allDetailsForScoreEntry.scores as ScoreMatchPlay[];
      const currentScore = scores.find(
        (s: ScoreMatchPlay) =>
          s.holeNumber === currentHole &&
          s.scoreByPlayerId === currentUser?.playerId
      );

      if (currentScore) {
        setPlayerScores(
          currentScore.scores?.map((s) => ({
            playerId: s.playerId,
            score: s.grossScore,
            isConceded: s.isConceded,
            playerHandicap: Number(
              flightDetails.players?.find((p) => p.playerId === s.playerId)
                ?.player.playerHandicap ?? 0
            ),
          }))
        );
      } else {
        const flightDetails =
          allDetailsForScoreEntry?.flightDetails as FlightDetailsMatchPlay;
        const playerScoresData =
          flightDetails?.players?.map((p) => ({
            playerId: p.playerId,
            score: 0,
            isConceded: false,
            playerHandicap: Number(p.player.playerHandicap),
          })) || [];
        setPlayerScores(playerScoresData);
      }
    }
  }, [allDetailsForScoreEntry, currentHole, currentUser]);

  const isValidScores = (scores: PlayerScore[]) => {
    if (!scores) return false;
    const selfScore = scores.find(p => p.playerId === currentUser?.playerId && (p.score > 0 || p.isConceded));
    if (!selfScore) return false;
    const otherScores = scores.filter(p => p.playerId !== currentUser?.playerId && (p.score > 0 || p.isConceded));
    if (otherScores.length < 1) return false;
    return true;
  }

  const handleScoreChange = (playerId: string, increment: boolean) => {
    setPlayerScores((prev) => {
      let a: any[] = prev;
      if (!a || a.length === 0) {
        const flightDetails =
          allDetailsForScoreEntry?.flightDetails as FlightDetailsMatchPlay;
        a =
          flightDetails?.players?.map((p) => ({
            playerId: p.playerId,
            score: 0,
            isConceded: false,
            playerHandicap: Number(p.player.playerHandicap),
          })) || [];
      }
      return a.map((ps) => {
        if (ps.playerId !== playerId) return ps;

        if (ps.score === 0) {
          return { ...ps, score: increment ? 1 : 12 };
        }

        const newScore = increment
          ? Math.min((ps.score as number) + 1, 12)
          : Math.max((ps.score as number) - 1, 1);
        return { ...ps, score: newScore };
      });
    });
  };

  const toggleHoleConceded = (playerId: string) => {
    setPlayerScores((prev) =>
      prev.map((ps) => {
        if (ps.playerId !== playerId) return ps;
        return { ...ps, isConceded: !ps.isConceded, score: 0 };
      })
    );
  };

  const handleSaveScore = (setNext = true) => {
    if (!allDetailsForScoreEntry || !currentUser || !playerScores) return;
    // if (!playerScores.every((ps) => ps.score > 0 || ps.isConceded)) return;
    if (!isValidScores(playerScores)) return;

    const currentHoleData = allDetailsForScoreEntry.holes.find(
      (h) => h.holeNumber === currentHole
    );
    if (!currentHoleData) return;
    const scores = (allDetailsForScoreEntry.scores ?? []) as ScoreMatchPlay[];
    const flightDetails =
      allDetailsForScoreEntry.flightDetails as FlightDetailsMatchPlay;

    const existingScore = scores.find(
      (s) =>
        s.holeNumber === currentHole &&
        s.scoreByPlayerId === currentUser.playerId
    );

    let hasChanges = false;
    if (existingScore) {
      hasChanges = playerScores.some((ps) => {
        const es = existingScore.scores.find((s) => s.playerId === ps.playerId);
        return (
          !es || es.grossScore !== ps.score || es.isConceded !== ps.isConceded
        );
      });
    }
    const wld = getWLDSingleHoleScore(playerScores, currentHoleData);
    const matchResults = getScoresBetweenPlayers(playerScores, currentHoleData, currentUser?.playerId??"");
    if (hasChanges && existingScore) {
      openDialog({
        title: "Confirm Save",
        message:
          "You have unsaved changes for this hole. Are you sure you want to save?",
        onConfirm: () => {
          // update.
          updateMatchPlayScore({
            ...existingScore,
            matchResults,
            scores: playerScores.map((ps) => {
              return {
                playerId: ps.playerId,
                playerHandicap: ps.playerHandicap,
                teamId:
                  flightDetails.players?.find((p) => p.playerId === ps.playerId)
                    ?.team.id ?? "",
                grossScore: ps.score,
                isConceded: ps.isConceded,
                winCount: wld.find((w) => w.playerId === ps.playerId)?.win ?? 0,
                loseCount:
                  wld.find((w) => w.playerId === ps.playerId)?.loss ?? 0,
                drawCount:
                  wld.find((w) => w.playerId === ps.playerId)?.draw ?? 0,
                scoreByType:
                  currentUser.playerId === ps.playerId ? "self" : "mark",
              };
            }),
            isUploaded: false,
          });
        },
      });
    } else if (!existingScore || !existingScore.isUploaded) {
      const matchSetup = allDetailsForScoreEntry.matchSetupConfig;

      const scoreObj: ScoreMatchPlay = {
        id: uuid(),
        matchId: matchSetup.matchId,
        seasonId: matchSetup.seasonId,
        holeNumber: currentHole,
        holePar: getCurrentHoleData(currentHole)?.par ?? 0,
        holeStrokeIndex: getCurrentHoleData(currentHole)?.strokeIndex ?? 0,
        scoreDateTime: formatToTimeStamp(new Date()),
        scoreByPlayerId: currentUser.playerId ?? "",
        scoreByType: "player",
        matchResults,
        scores: playerScores.map((ps) => ({
          playerId: ps.playerId,
          playerHandicap: ps.playerHandicap,
          teamId:
            flightDetails.players?.find((p) => p.playerId === ps.playerId)?.team
              .id ?? "",
          grossScore: ps.score,
          isConceded: ps.isConceded,
          winCount: wld.find((w: any) => w.playerId === ps.playerId)?.win ?? 0,
          loseCount:
            wld.find((w: any) => w.playerId === ps.playerId)?.loss ?? 0,
          drawCount:
            wld.find((w: any) => w.playerId === ps.playerId)?.draw ?? 0,
          scoreByType: currentUser.playerId === ps.playerId ? "self" : "mark",
        })),
        scoreStatus: "complete",
        scoreComment: "",
        isUploaded: false,
        uploadDateTime: "",
        scoreEnteredByPlayerId: currentUser.playerId,
      };
      addMatchPlayScore(scoreObj);
    }

    if (setNext) handleNextHole(false);
  };

  const handleUploadScores = () => {
    uploadMatchPlayScoresByPlayer();
  };

  // New function to sort players with current user first
  const getSortedPlayers = (players: FlightDetailsMatchPlay["players"]) => {
    if (!players || !currentUser) return players;

    return [...players].sort((a, b) => {
      if (a.playerId === currentUser.playerId) return -1;
      if (b.playerId === currentUser.playerId) return 1;
      return 0;
    });
  };

  return (
    <div className="py-0 px-1 pb-6">
      {!allDetailsForScoreEntry && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-8">
          <div className="text-center text-gray-900 dark:text-white">
            <Trophy className="h-12 w-12 text-blue-500" />
            <h2 className="text-xl font-semibold mb-2">
              No Match in Progress.
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              You are not currently in a match. Please check back later.
            </p>
          </div>
        </div>
      )}

      {allDetailsForScoreEntry &&
        allDetailsForScoreEntry.matchSetupConfig &&
        allDetailsForScoreEntry.flightDetails && (
          <>
            {/* Hole Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-sm shadow-md p-4 ">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {allDetailsForScoreEntry.matchSetupConfig.courseName} -{" "}
                  {showDateTimeShort(
                    `${allDetailsForScoreEntry.matchSetupConfig.date}${allDetailsForScoreEntry.matchSetupConfig.time}`
                  )}
                </h2>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-8">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-between w-full mb-4">
                  <button
                    onClick={handlePrevHole}
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white flex justify-center items-center space-x-4">
                    <span>Hole {currentHole}</span>
                    <div className="text-lg text-gray-500 dark:text-gray-400">
                      Par {getCurrentHoleData(currentHole)?.par}
                    </div>
                    <div className="text-lg text-gray-500 dark:text-gray-400">
                      SI:{getCurrentHoleData(currentHole)?.strokeIndex}
                    </div>
                  </div>
                  <button
                    onClick={() => handleNextHole(true)}
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>

                <div className="text-center mb-6">
                  <div className="mt-1 flex justify-center space-x-4 text-sm">
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      Men: {getCurrentHoleData(currentHole)?.men}y
                    </span>
                    <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                      Senior: {getCurrentHoleData(currentHole)?.senior}y
                    </span>
                    <span className="text-pink-600 dark:text-pink-400 font-medium">
                      Ladies: {getCurrentHoleData(currentHole)?.ladies}y
                    </span>
                  </div>
                </div>

                {/* Player Scores */}
                <div className="grid grid-cols-1 gap-6 mb-6 w-full max-w-2xl mx-auto">
                  {getSortedPlayers(
                    (
                      allDetailsForScoreEntry?.flightDetails as FlightDetailsMatchPlay
                    )?.players
                  )?.map((flightPlayer) => (
                    <div
                      key={flightPlayer.playerId}
                      className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0 pb-4 ${
                        flightPlayer.playerId === currentUser?.playerId
                          ? "bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
                          : ""
                      }`}
                    >
                      <div className="flex flex-col justify-center items-center mb-4">
                        <div className="flex">
                          <PlayerRecord
                            member={flightPlayer.player}
                            showHandicapAndMsl={false}
                          />
                          <span className="ml-2 text-gray-600 dark:text-gray-300">
                            (HC: {flightPlayer.player.playerHandicap})
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-center space-x-4">
                        <button
                          onClick={() =>
                            handleScoreChange(flightPlayer.playerId, false)
                          }
                          disabled={
                            playerScores?.find(
                              (ps) => ps.playerId === flightPlayer.playerId
                            )?.isConceded
                          }
                          className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                        >
                          -
                        </button>
                        <div className="w-20 text-center text-3xl font-bold text-gray-900 dark:text-white">
                          {playerScores?.find(
                            (ps) => ps.playerId === flightPlayer.playerId
                          )?.isConceded
                            ? "CON"
                            : playerScores?.find(
                                (ps) => ps.playerId === flightPlayer.playerId
                              )?.score || "-"}
                        </div>
                        <button
                          onClick={() =>
                            handleScoreChange(flightPlayer.playerId, true)
                          }
                          disabled={
                            playerScores?.find(
                              (ps) => ps.playerId === flightPlayer.playerId
                            )?.isConceded
                          }
                          className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                        >
                          +
                        </button>
                        <button
                          onClick={() =>
                            toggleHoleConceded(flightPlayer.playerId)
                          }
                          className={`px-4 py-2 rounded-lg text-sm font-medium ${
                            playerScores?.find(
                              (ps) => ps.playerId === flightPlayer.playerId
                            )?.isConceded
                              ? "bg-red-100 text-red-700 dark:bg-red-700 dark:text-red-100"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          Hole Conceded
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleSaveScore()}
                  disabled={!isValidScores(playerScores)}
                  className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white py-3 rounded-lg font-medium flex items-center justify-center disabled:opacity-50 transition-colors"
                >
                  <Save className="h-5 w-5 mr-2" />
                  Save Scores
                </button>
              </div>
            </div>

            <div className="mt-8">
              <MatchPlayScoreList
                scores={allDetailsForScoreEntry.scores as ScoreMatchPlay[]}
                holes={allDetailsForScoreEntry.holes}
                players={
                  (
                    allDetailsForScoreEntry.flightDetails as FlightDetailsMatchPlay
                  )?.players
                }
                onUpload={handleUploadScores}
              />
            </div>
          </>
        )}
    </div>
  );
};
